import React, { FC, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import moment from 'moment-timezone'
import { Button } from 'transfix-ui/components/Button'
import { Box, Typography, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { InlineEditContainer } from 'transfix-ui'
import { FORMATS } from 'constants/datetime'
import { TOnSubmitField } from './types'

export interface IInlineEditDisplayProps {
  label: React.ReactNode
  displayValue: React.ReactNode | string
  children: React.ReactNode
  dataTest: string
  name: string | string[]
  onSubmit: TOnSubmitField
  readOnly?: boolean
  editLabel?: React.ReactNode
  onEditChange?: (isEditMode: boolean) => void
  sectionName?: string
}

const useInlineEditDisplayStyles = makeStyles(theme => ({
  editContainer: {
    border: '1px solid',
    borderColor: brandColors.coolGray4,
    borderRadius: '4px',
    padding: theme.spacing(3),
  },
  valueContainer: {
    marginTop: theme.spacing(1),
  },
}))

/**
 * Stateful wrapper with default styles with display and edit views
 */
const InlineEditDisplay: FC<IInlineEditDisplayProps> = ({
  children,
  dataTest,
  displayValue,
  editLabel,
  label,
  name,
  onSubmit,
  readOnly = false,
  onEditChange = () => {},
  sectionName,
}) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const editModeHandler = () => {
    if (!isEditMode) {
      setIsEditMode(true)
      onEditChange(true)
    }
  }
  const classes = useInlineEditDisplayStyles()
  const cancelClickHandler = () => {
    setIsEditMode(false)
    onEditChange(false)
  }

  const { getValues, setError } = useFormContext()

  const handleEdit = async () => {
    const values = getValues()

    const valueKeys = Array.isArray(name) ? name : [name]

    const payload: typeof values = {}

    valueKeys.forEach(key => {
      payload[key] = values[key]
    })

    if (payload.owner) {
      if (!payload.owner.followerId || !payload.owner.followerType) {
        // set error and prevent calling mutation if owner has not been selected
        setError('owner', { message: 'Select an owner' })
        return
      }
      payload.owner = {
        followerId: payload.owner.followerId,
        followerType: payload.owner.followerType,
      }
    }

    if (name === 'dueAt') {
      const dueDate = moment(values.dueDate).format(FORMATS.dateFormatShort)

      payload.dueAt = moment(`${dueDate} ${values.dueTime}`)
        .tz(FORMATS.defaultTimeZone)
        .toISOString()
    }

    await onSubmit(payload)
    cancelClickHandler()
  }

  return (
    <InlineEditContainer
      dataTest={`${dataTest}-container`}
      onEditModeRequested={editModeHandler}
      editModeEnabled={!readOnly && isEditMode}
      editMode={
        <div className={classes.editContainer} data-dd-action-name={sectionName}>
          <Typography variant='subtitle1'>{editLabel || label}</Typography>
          <Box mt={1}>{children}</Box>
          <Box display='flex' justifyContent='flex-end' mt={3}>
            <Button color='tertiary' size='large' onClick={cancelClickHandler}>
              Cancel
            </Button>
            <Button
              color='primary'
              size='large'
              data-test={`${dataTest}-submit`}
              onClick={handleEdit}>
              Save
            </Button>
          </Box>
        </div>
      }>
      <div data-dd-action-name={sectionName}>
        <Typography variant='subtitle1'>{label}</Typography>
        <div className={classes.valueContainer}>{displayValue}</div>
      </div>
    </InlineEditContainer>
  )
}

export default InlineEditDisplay
